import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { type AdminRole } from '@avanta/shared/types/enterprises';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type ColumnDef, type ColumnFiltersState, type Table } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { EnterprisesService } from 'services/enterprises';
import { useEnterpriseId } from 'stores/user';
import { type AdminEnterprise } from 'types/enterprises';

const getRoleFilter = (columnFilters: ColumnFiltersState): AdminRole | undefined => {
  const roleFilterValue = columnFilters.find(filter => filter.id === 'role')?.value;

  return typeof roleFilterValue === 'string' ? roleFilterValue : undefined;
};

export const useFetchAdmins = (
  columns: ColumnDef<AdminEnterprise>[],
  pageSize: number = 10
): {
  globalFilter: string;
  query: UseQueryResult<AdminEnterprise[], Error>;
  table: Table<AdminEnterprise>;
  totalCount: number;
  setGlobalFilter: (value: string) => void;
} => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const enterpriseId = useEnterpriseId();
  const debouncedFilter = useDebounce(globalFilter, 500);
  const role = getRoleFilter(columnFilters);

  const { data: totalCount = 0 } = useQuery({
    queryKey: [QUERY_KEYS.FETCH_ADMINS_COUNT, enterpriseId, role],
    queryFn: () => EnterprisesService.getCountAdminsByEnterprise(enterpriseId, role),
    select: response => response.data.count,
    enabled: !!enterpriseId,
  });

  const query = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_ADMINS,
      enterpriseId,
      pagination.pageIndex,
      pagination.pageSize,
      debouncedFilter,
      role,
    ],
    queryFn: async () =>
      EnterprisesService.getAdminsByEnterprise(
        enterpriseId,
        pagination.pageIndex,
        pagination.pageSize,
        role
      ),
    select: response => response.data,
    placeholderData: previousData => previousData,
    enabled: !!enterpriseId,
  });

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [debouncedFilter, role]);

  const table = useServerPaginatedTable<AdminEnterprise>({
    columns,
    data: query.data ?? [],
    totalCount,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: setGlobalFilter,
    columnFilters,
    onColumnFiltersChange: setColumnFilters,
  });

  return {
    query,
    globalFilter,
    table,
    totalCount,
    setGlobalFilter,
  };
};
